// SPDX-FileCopyrightText: Copyright Orangebot, Inc.
// SPDX-License-Identifier: Apache-2.0

/**
 * Helpers for cleaning up call signaling Communications when the browser
 * is leaving the page. React lifecycle cleanup (`useEffect` return) is not
 * reliable for tab close, browser quit, or network drop — by the time React
 * runs cleanup, the page is already being destroyed and any async HTTP
 * request is cancelled.
 *
 * Use `fetch({ keepalive: true })` instead. The browser commits the request
 * to a separate pool that outlives the page. PATCH with a json-patch body
 * lets us update Communication.status + Communication.statusReason
 * atomically.
 */

import type { MedplumClient } from '@medplum/core';
import { CARECOMM_OUTCOME, carecommOutcomeReason, type CareCommOutcomeCode } from './organizationSettings';

interface UnloadPatchOptions {
  medplum: MedplumClient;
  communicationId: string;
  outcome: CareCommOutcomeCode;
}

/**
 * Patch a Communication to status=completed + statusReason in a way that
 * survives `pagehide`/`unload`. Returns synchronously; the browser keeps
 * the request alive until the response (or timeout) — do NOT await this on
 * an unload path because awaiting can be cancelled when the page goes away.
 */
export function patchCommunicationOnUnload(opts: UnloadPatchOptions): void {
  const { medplum, communicationId, outcome } = opts;
  if (!communicationId) return;

  const accessToken = medplum.getAccessToken();
  if (!accessToken) return;

  const baseUrl = medplum.getBaseUrl();
  const url = `${baseUrl}fhir/R4/Communication/${communicationId}`;
  const body = JSON.stringify([
    { op: 'replace', path: '/status', value: 'completed' },
    { op: 'add', path: '/statusReason', value: carecommOutcomeReason(outcome) },
  ]);

  try {
    fetch(url, {
      method: 'PATCH',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json-patch+json',
        Authorization: `Bearer ${accessToken}`,
      },
      body,
    }).catch(() => {
      // best-effort; nothing useful we can do during unload
    });
  } catch {
    // ignore
  }
}

/**
 * Patch via the normal awaited path. Use for in-app event handlers
 * (Agora disconnect, normal hangup) where we have time to await.
 */
export async function patchCommunicationOutcome(
  medplum: MedplumClient,
  communicationId: string,
  outcome: CareCommOutcomeCode
): Promise<void> {
  if (!communicationId) return;
  await medplum.patchResource('Communication', communicationId, [
    { op: 'replace', path: '/status', value: 'completed' },
    { op: 'add', path: '/statusReason', value: carecommOutcomeReason(outcome) },
  ]);
}

export const CARECOMM_OUTCOMES = CARECOMM_OUTCOME;

/**
 * The extension a live call stamps to show it is still being held.
 *
 * Exported so the sweeper's TTL and this interval can be reasoned about
 * together: the sweeper must tolerate at least one missed beat.
 */
export const CALL_HEARTBEAT_EXTENSION =
  'https://nuloucarepoint.com/fhir/StructureDefinition/call-heartbeat';

/** How often a live call stamps itself. See the sweeper's TTL. */
export const CALL_HEARTBEAT_INTERVAL_MS = 120_000;

/**
 * Mark a call as still in progress.
 *
 * `expire-stale-call-comms` completes any call Communication whose
 * `_lastUpdated` is older than its TTL, and nothing else touches the resource
 * between the call starting and it ending — so without this, a consultation
 * longer than the TTL is marked `completed / auto-timeout` while the clinician
 * is still speaking, and its recorded duration is the TTL rather than the
 * call. For time-based billing that is not a cosmetic error.
 *
 * Read-modify-write rather than a JSON Patch on `/extension`, because the
 * Agora channel lives in that same array and a blind write would drop it.
 * Only the window holding the call beats, so there is one writer and the
 * race this would otherwise invite does not arise.
 */
export async function sendCallHeartbeat(
  medplum: MedplumClient,
  communicationId: string
): Promise<void> {
  const communication = await medplum.readResource('Communication', communicationId);

  /*
   * A call that has already ended must not be resurrected. Without this, a
   * beat racing a hangup would flip the record back to in-progress and the
   * sweeper would be the only thing that ever closed it.
   */
  if (communication.status !== 'in-progress') {
    return;
  }

  const others = (communication.extension ?? []).filter(
    (extension) => extension.url !== CALL_HEARTBEAT_EXTENSION
  );
  await medplum.updateResource({
    ...communication,
    extension: [
      ...others,
      { url: CALL_HEARTBEAT_EXTENSION, valueDateTime: new Date().toISOString() },
    ],
  });
}
